import { memo, useCallback, useMemo } from 'react';
import { Grid, Typography } from '@mui/material';

import { NumberInput } from '@/modules/common/components/inputs';

import { ScaleDraft } from '../../types';
import { ReferenceImageServiceFactory } from '../../service';

type Props = {
  pointA: ScaleDraft['pointA'];
  pointB: ScaleDraft['pointB'];
  disabled: boolean;
};

const CoordinatesInputComponent = ({ pointA, pointB, disabled }: Props) => {
  const referenceImageSvc = useMemo(
    () => ReferenceImageServiceFactory.getInstance().createService(),
    [],
  );

  const onPointAXChange = useCallback(
    (newValue: number) => {
      if (!pointA) return;
      const newPointA = { ...pointA, x: newValue };
      referenceImageSvc.updateScalePointA(newPointA);
    },
    [pointA, referenceImageSvc],
  );

  const onPointAYChange = useCallback(
    (newValue: number) => {
      if (!pointA) return;
      const newPointA = { ...pointA, y: newValue };
      referenceImageSvc.updateScalePointA(newPointA);
    },
    [pointA, referenceImageSvc],
  );
  
  const onPointBXChange = useCallback(
    (newValue: number) => {
      if (!pointB) return;
      const newPointB = { ...pointB, x: newValue };
      referenceImageSvc.updateScalePointB(newPointB);
    },
    [pointB, referenceImageSvc],
  );

  const onPointBYChange = useCallback(
    (newValue: number) => {
      if (!pointB) return;
      const newPointB = { ...pointB, y: newValue };
      referenceImageSvc.updateScalePointB(newPointB);
    },
    [pointB, referenceImageSvc],
  );

  if (!pointA || !pointB) return null;

  return (
    <Grid
      container
      alignItems='center'
      justifyContent='space-between'
      rowGap={1}
      columns={36}
      sx={{ '&:hover': { cursor: 'default' } }}
    >
      <Grid item xs={36}>
        <Typography fontSize='11px'>Start point</Typography>
      </Grid>

      <Grid item xs={3}>
        <Typography fontSize='10px'>X</Typography>
      </Grid>

      <Grid item xs={14}>
        <NumberInput
          value={pointA.x}
          unit='mm'
          errorType='floating'
          disabled={disabled}
          onChange={onPointAXChange}
        />
      </Grid>

      <Grid item xs={2}>
        {/* Intentionally Empty */}
      </Grid>

      <Grid item xs={3} justifyContent='center'>
        <Typography fontSize='10px'>Y</Typography>
      </Grid>

      <Grid item xs={14}>
        <NumberInput
          value={pointA.y}
          unit='mm'
          errorType='floating'
          disabled={disabled}
          onChange={onPointAYChange}
        />
      </Grid>
    
      <Grid item xs={36}>
        <Typography fontSize='11px'>End point</Typography>
      </Grid>

      <Grid item xs={3}>
        <Typography fontSize='10px'>X</Typography>
      </Grid>

      <Grid item xs={14}>
        <NumberInput
          value={pointB.x}
          unit='mm'
          errorType='floating'
          disabled={disabled}
          onChange={onPointBXChange}
        />
      </Grid>

      <Grid item xs={2}>
        {/* Intentionally Empty */}
      </Grid>

      <Grid item xs={3} justifyContent='center'>
        <Typography fontSize='10px'>Y</Typography>
      </Grid>

      <Grid item xs={14}>
        <NumberInput
          value={pointB.y}
          unit='mm'
          errorType='floating'
          disabled={disabled}
          onChange={onPointBYChange}
        />
      </Grid>
    </Grid>
  );
}

export const CoordinatesInput = memo(CoordinatesInputComponent);
